import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Modal,
  Tag,
} from "antd";
import { CloseOutlined, ExclamationCircleOutlined, PlusOutlined, CheckOutlined } from "@ant-design/icons";
import API from "../../../services/api";
import { useCloudinaryUpload } from "../../../hooks/useCloudinaryUpload";
import { useState, useEffect } from "react";
import "./AddStall.css";
import { useNavigate } from "react-router-dom";
import LocationField from "../../LocationField";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STALL_TYPES = ["food", "retail", "exhibition", "service", "other"];

// Duration options per pricing type:
// rent  → daily, weekly, monthly, yearly
// lease → monthly, yearly
// sale  → (none — no duration shown)
const DURATION_TYPES_BY_PRICING = {
  rent: ["daily", "weekly", "monthly", "yearly"],
  lease: ["monthly", "yearly"],
  sale: [],
};

// Predefined facilities for quick add
const QUICK_FACILITIES = [
  "Power Supply",
  "Water Supply",
  "Seating Area",
  "Restroom Nearby",
  "Waste Management",
  "Lighting",
  "Ventilation",
  "Storage Space",
  "Parking",
  "Security",
  "Wi-Fi",
  "Fire Safety",
];

function AddStall() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);

  const {
    uploadedThumb,
    uploadedImages,
    imageFileList,
    setImageFileList,
    uploading,
    uploadThumbnail,
    uploadImages,
    removeImage
  } = useCloudinaryUpload("stalls", message);

  // Facilities state
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [customFacility, setCustomFacility] = useState("");

  // Track which quick facility buttons were just clicked to show visual feedback
  const [clickedFacility, setClickedFacility] = useState(null);

  // Custom stall type
  const [customStallType, setCustomStallType] = useState("");
  const [showCustomStallType, setShowCustomStallType] = useState(false);

  // Image upload errors
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imagesError, setImagesError] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(null);

  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  const handleBack = () => {
    const values = form.getFieldsValue();

    const hasData = Object.values(values).some(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    );

    if (!hasData && facilitiesList.length === 0) {
      window.history.back();
      return;
    }

    Modal.confirm({
      title: "Leave this page?",
      icon: <ExclamationCircleOutlined />,
      content: "All your unsaved changes will be lost if you go back.",
      okText: "Yes, go back",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => {
        window.history.back();
      },
    });
  };

  // Facility management functions
  const addQuickFacility = (facility) => {
    setClickedFacility(facility);
    setTimeout(() => setClickedFacility(null), 300);

    if (!facilitiesList.includes(facility)) {
      const newFacilities = [...facilitiesList, facility];
      setFacilitiesList(newFacilities);
      form.setFieldsValue({ facilities: newFacilities.join(", ") });
    }
  };

  const addCustomFacility = () => {
    const facility = customFacility.trim();
    if (!facility) {
      message.warning("Please enter a facility name");
      return;
    }

    if (!/^[A-Za-z\s.-]+$/.test(facility)) {
      message.error("Facility name can only contain letters, spaces, . and -");
      return;
    }

    if (!facilitiesList.includes(facility)) {
      const newFacilities = [...facilitiesList, facility];
      setFacilitiesList(newFacilities);
      form.setFieldsValue({ facilities: newFacilities.join(", ") });
      setCustomFacility("");
    } 
  };

  const removeFacility = (facilityToRemove) => {
    const newFacilities = facilitiesList.filter(f => f !== facilityToRemove);
    setFacilitiesList(newFacilities);
    form.setFieldsValue({ facilities: newFacilities.join(", ") });
  };

  // Steps — Duration merged into Pricing (step 2)
  const steps = [
    "Description",
    "Details",
    "Pricing & Duration",
    "Location",
    "Facilities",
    "Contact",
    "Images",
  ];

  // stepFields used by form.validateFields per step
  const stepFields = [
    ["title", "description"],                                                              // Step 0
    ["stallType", "areaSqFt", "allowedBusinessTypes"],                                    // Step 1
    ["pricingType", "durationType", "startDate", "endDate", "price", "deposit", "negotiable"], // Step 2
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],           // Step 3
    ["facilities"],                                                                        // Step 4
    ["contactName", "phone", "email"],                                                     // Step 5
    ["thumbnail", "images"],                                                               // Step 6
  ];

  const next = async () => {
    // Special validation for stall type step
    if (current === 1) {
      if (showCustomStallType && (!customStallType || customStallType.trim() === "")) {
        message.error("Please enter custom stall type");
        return;
      }
    }

    // Special validation for Pricing & Duration step (step 2)
    if (current === 2) {
      const pricingType = form.getFieldValue("pricingType");

      if (pricingType === "rent") {
        const deposit = form.getFieldValue("deposit");
        if (!deposit) {
          message.error("Please enter deposit amount for rent");
          return;
        }
      }
    }

    // Special validation for facilities step (step 4)
    if (current === 4) {
      if (facilitiesList.length === 0) {
        message.error("Please add at least one facility");
        return;
      }
      setCurrent(current + 1);
      window.scrollTo(0, 0);
      return;
    }

    // Special validation for images step (step 6)
    if (current === 6) {
      if (!uploadedThumb) {
        setThumbnailError(true);
        message.error("Please upload a thumbnail image");
        return;
      }

      if (!uploadedImages || uploadedImages.length === 0) {
        setImagesError(true);
        message.error("Please upload at least one image");
        return;
      }

      setThumbnailError(false);
      setImagesError(false);
    }

    try {
      // For step 2 (Pricing & Duration), only validate durationType/startDate/endDate
      // if pricingType is not sale
      if (current === 2) {
        const pricingType = form.getFieldValue("pricingType");
        let fieldsToValidate = ["pricingType", "price", "negotiable"];
        if (pricingType === "rent") {
          fieldsToValidate = [...fieldsToValidate, "deposit", "durationType", "startDate", "endDate"];
        } else if (pricingType === "lease") {
          fieldsToValidate = [...fieldsToValidate, "durationType", "startDate", "endDate"];
        }
        // sale → no duration fields needed
        await form.validateFields(fieldsToValidate);
      } else {
        await form.validateFields(stepFields[current]);
      }

      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({
        content: firstError,
        key: "validation-error",
        duration: 2.5,
      });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const pricingType = values.pricingType || "rent";

      const data = {
        title: values.title,
        description: values.description,
        stallDetails: {
          stallType: values.stallType,
          areaSqFt: Number(values.areaSqFt),
          allowedBusinessTypes: values.allowedBusinessTypes
            ? values.allowedBusinessTypes.split(",").map((a) => a.trim()).filter(Boolean)
            : [],
        },
        // For sale: pass null values; for rent/lease: pass the chosen values
        durationDetails:
          pricingType === "sale"
            ? { durationType: null, startDate: null, endDate: null }
            : {
              durationType: values.durationType,
              startDate: values.startDate,
              endDate: values.endDate,
            },
        location: {
          address: values.address,
          locality: values.locality?.trim(),
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        pricing: {
          type: pricingType,
          price: Number(values.price),
          ...(pricingType === "rent" && values.deposit ? { deposit: Number(values.deposit) } : {}),
          negotiable: !!values.negotiable,
        },
        facilities: facilitiesList,
        contactDetails: {
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
        },
      };

      await API.post("/stalls", {
        ...data,
        thumbnail: uploadedThumb?.url,
        images: uploadedImages.map(img => img.url),
      });

      navigate("/success", {
        state: { type: "stall" }
      });
      setFacilitiesList([]);
      setCustomFacility("");
      setThumbnailError(false);
      setImagesError(false);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".stall-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  return (
    <div className="stall-page">
      <div className="stall-container">
        <Card className="stall-card">
          {/* Card Header */}
          <div className="stall-card-header">
            <div className="stall-header-left" />
            <div className="stall-header-center">
              <Title className="stall-title">Add Stall</Title>
            </div>
            <div className="stall-header-right">
              <button className="stall-back-btn" onClick={handleBack} type="button">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="stall-stepper">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`stall-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
              >
                <div className="stall-step-line-left" />
                <div className="stall-step-inner">
                  <div className="stall-circle">
                    {current > i ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="stall-step-label">{step}</span>
                </div>
                <div className="stall-step-line-right" />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="stall-progress-bar">
            <div
              className="stall-progress-fill"
              style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className="stall-progress-label">
            Step {current + 1} of {steps.length} — {steps[current]}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            validateTrigger="onSubmit"
            className="stall-form"
          >
            {/* Step 0: Description */}
            <div style={getStepStyle(0)}>
              <Form.Item
                name="title"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Stall Title
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter a title for your stall" },
                  { min: 5, message: "Title must be at least 5 characters long" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                    message: "Title must include at least one letter and can contain numbers or symbols like # . & -",
                  },
                ]}
              >
                <Input placeholder="e.g. Premium Food Stall at City Mall" />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Description
                  </span>
                }
                rules={[
                  { required: true, message: "Please provide a description" },
                  { min: 20, message: "Description should be at least 20 characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <TextArea rows={5} placeholder="Describe your stall, business potential, location advantages, and unique selling points..." />
              </Form.Item>
            </div>

            {/* Step 1: Details */}
            <div style={getStepStyle(1)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="stallType"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Stall Type
                      </span>
                    }
                    rules={[{ required: true, message: "Please select a stall type" }]}
                    help={null}
                    validateStatus="success"
                  >
                    <div className="stall-custom-select">
                      <div
                        className="stall-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === "stallType"
                              ? null
                              : "stallType"
                          )
                        }
                      >
                        {form.getFieldValue("stallType")
                          ? form
                            .getFieldValue("stallType")
                            .charAt(0)
                            .toUpperCase() +
                          form
                            .getFieldValue("stallType")
                            .slice(1)
                          : "Select stall type"}

                        <span className="stall-custom-arrow">
                          ⌄
                        </span>
                      </div>

                      {openDropdown === "stallType" && (
                        <div className="stall-custom-dropdown">
                          {STALL_TYPES.map((item) => (
                            <div
                              key={item}
                              className="stall-custom-dropdown-item"
                              onClick={() => {
                                form.setFieldValue(
                                  "stallType",
                                  item
                                );

                                if (item === "other") {
                                  setShowCustomStallType(true);
                                } else {
                                  setShowCustomStallType(false);
                                  setCustomStallType("");
                                }

                                setOpenDropdown(null);
                              }}
                            >
                              {item.charAt(0).toUpperCase() +
                                item.slice(1)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                  {showCustomStallType && (
                    <Form.Item
                      name="customStallType"
                      label="Enter Custom Stall Type"
                      rules={[
                        { required: true, message: "Please enter custom stall type" },
                        { min: 2, message: "Stall type must be at least 2 characters" },
                        { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                      ]}
                      style={{ marginTop: -8 }}
                    >
                      <Input
                        placeholder="e.g. Art & Craft, Furniture, Electronics"
                        value={customStallType}
                        onChange={(e) => {
                          setCustomStallType(e.target.value);
                          form.setFieldValue("stallType", e.target.value);
                        }}
                      />
                    </Form.Item>
                  )}
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="areaSqFt"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Area (sq ft)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the area in sq ft" },
                      { type: "number", min: 10, message: "Area must be at least 10 sq ft" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 120" min={10} />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="allowedBusinessTypes"
                    label="Allowed Business Types"
                    extra="Separate each business type with a comma — e.g. Food, Retail, Electronics"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const items = value.split(",").map(v => v.trim()).filter(Boolean);
                          const invalid = items.some(v => !/^[A-Za-z\s]+$/.test(v));
                          if (invalid) return Promise.reject("Business types should only contain letters");
                          return Promise.resolve();
                        },
                      },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="Food, Retail, Electronics, etc." />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 2: Pricing & Duration (MERGED) */}
            <div style={getStepStyle(2)}>
              {/* Pricing Type */}
              <Form.Item
                name="pricingType"
                className="stall-pricing-dropdown-wrapper"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Pricing Type
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select a pricing type" }]}
                help={null}
                validateStatus="success"
              >
                <div className="stall-custom-select">
                  <div
                    className="stall-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "pricingType"
                          ? null
                          : "pricingType"
                      )
                    }
                  >
                    {form.getFieldValue("pricingType")
                      ? form
                        .getFieldValue("pricingType")
                        .charAt(0)
                        .toUpperCase() +
                      form
                        .getFieldValue("pricingType")
                        .slice(1)
                      : "Select pricing type"}

                    <span className="stall-custom-arrow">
                      ⌄
                    </span>
                  </div>

                  {openDropdown === "pricingType" && (
                    <div className="stall-custom-dropdown">
                      {[
                        {
                          label: "Rent",
                          value: "rent",
                        },
                        {
                          label: "Lease",
                          value: "lease",
                        },
                        {
                          label: "Sale",
                          value: "sale",
                        },
                      ].map((item) => (
                        <div
                          key={item.value}
                          className="stall-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "pricingType",
                              item.value
                            );

                            if (
                              item.value !== "rent"
                            ) {
                              form.setFieldsValue({
                                deposit: undefined,
                              });
                            }

                            if (
                              item.value === "sale"
                            ) {
                              form.setFieldsValue({
                                durationType:
                                  undefined,
                                startDate:
                                  undefined,
                                endDate:
                                  undefined,
                              });
                            }

                            if (
                              item.value === "lease"
                            ) {
                              const currentDuration =
                                form.getFieldValue(
                                  "durationType"
                                );

                              if (
                                currentDuration &&
                                ![
                                  "monthly",
                                  "yearly",
                                ].includes(
                                  currentDuration
                                )
                              ) {
                                form.setFieldsValue({
                                  durationType:
                                    undefined,
                                });
                              }
                            }

                            setOpenDropdown(null);
                          }}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>

              {/* Duration Type — shown only for rent and lease */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.pricingType !== curr.pricingType}
              >
                {({ getFieldValue }) => {
                  const pricingType = getFieldValue("pricingType");
                  if (pricingType === "sale" || !pricingType) return null;

                  const availableDurations = DURATION_TYPES_BY_PRICING[pricingType] || [];

                  return (
                    <Form.Item
                      name="durationType"
                      className="stall-duration-dropdown-wrapper"
                      label={
                        <span className="stall-label-with-badge">
                          <span className="stall-required-star">*</span>
                          Duration Type
                        </span>
                      }
                      required
                      rules={[{ required: true, message: "Please select a duration type" }]}
                      help={null}
                      validateStatus="success"
                    >
                      <div className="stall-custom-select">
                        <div
                          className="stall-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "durationType"
                                ? null
                                : "durationType"
                            )
                          }
                        >
                          {form.getFieldValue("durationType")
                            ? form
                              .getFieldValue("durationType")
                              .charAt(0)
                              .toUpperCase() +
                            form
                              .getFieldValue("durationType")
                              .slice(1)
                            : "Select duration type"}

                          <span className="stall-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown === "durationType" && (
                          <div className="stall-custom-dropdown">
                            {availableDurations.map((item) => (
                              <div
                                key={item}
                                className="stall-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "durationType",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  );
                }}
              </Form.Item>

              {/* Start & End Date — shown only for rent and lease */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.pricingType !== curr.pricingType}
              >
                {({ getFieldValue }) => {
                  const pricingType = getFieldValue("pricingType");
                  if (pricingType === "sale" || !pricingType) return null;

                  return (
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="startDate"
                          label={
                            <span className="stall-label-with-badge">
                              <span className="stall-required-star">*</span>
                              Start Date
                            </span>
                          }
                          required
                          rules={[{ required: true, message: "Please select start date" }]}
                          help={null}
                          validateStatus="success"
                        >
                          <Input type="date" placeholder="YYYY-MM-DD" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="endDate"
                          label={
                            <span className="stall-label-with-badge">
                              <span className="stall-required-star">*</span>
                              End Date
                            </span>
                          }
                          required
                          dependencies={["startDate"]}
                          rules={[
                            { required: true, message: "Please select end date" },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const startDate = getFieldValue("startDate");
                                if (!value || !startDate) return Promise.resolve();
                                if (new Date(value) <= new Date(startDate)) {
                                  return Promise.reject("End date must be after start date");
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          help={null}
                          validateStatus="success"
                        >
                          <Input type="date" placeholder="YYYY-MM-DD" />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>

              {/* Price & Deposit */}
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="price"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Price (₹)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the price" },
                      { type: "number", min: 1, message: "Price must be greater than 0" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 25000" min={1} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.pricingType !== currentValues.pricingType ||
                      prevValues.price !== currentValues.price
                    }
                  >
                    {({ getFieldValue }) => {
                      const pricingType = getFieldValue("pricingType");
                      if (pricingType === "rent") {
                        return (
                          <Form.Item
                            name="deposit"
                            label={
                              <span className="stall-label-with-badge">
                                <span className="stall-required-star">*</span>
                                Deposit (₹)
                              </span>
                            }
                            required
                            rules={[
                              { required: true, message: "Please enter the deposit amount" },
                              { type: "number", min: 0, message: "Deposit cannot be negative" },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const price = getFieldValue("price");
                                  if (value !== undefined && price !== undefined && value !== null && value <= price) {
                                    return Promise.reject(new Error("Deposit must be greater than price"));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="e.g. 50000"
                              min={0}
                            />
                          </Form.Item>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Negotiable" className="stall-switch-item">
                <Form.Item name="negotiable" valuePropName="checked" noStyle help={null}>
                  <Switch />
                </Form.Item>
                <span className="stall-switch-label">Price is open to negotiation</span>
              </Form.Item>
            </div>

            {/* Step 3: Location */}
            <div style={getStepStyle(3)}>
              <Form.Item
                name="address"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Address
                  </span>
                }
                required
                rules={[{ required: true, message: "Please enter the Address" }]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Shop No. 45, City Mall, Indiranagar, Bengaluru" />
              </Form.Item>

              <Form.Item
                name="locality"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Locality
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter locality" },
                  { min: 2, message: "Locality must be at least 2 characters" },
                  { max: 100, message: "Locality cannot exceed 100 characters" },
                  {
                    pattern: /^[A-Za-z0-9\s().-]*$/,
                    message:
                      "Locality can only contain letters, numbers, spaces, (), . and -",
                  },
                ]}
              >
                <LocationField
                  form={form}
                  message={message}
                  setAutoDetecting={setAutoDetecting}
                />
              </Form.Item>

              <Form.Item
                name="city"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    City
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the city" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Bengaluru" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        State
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the State" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. Karnataka" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="country"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Country
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the country" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. India" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="pincode"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Pincode
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the pincode" },
                  { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="latitude"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Latitude
                      </span>
                    }
                    required
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.reject("Latitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid latitude");
                          if (value < -90 || value > 90)
                            return Promise.reject("Latitude must be between -90 and 90");
                          return Promise.resolve();
                        },
                      },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-90}
                      max={90}
                      step={0.000001}
                      placeholder="e.g. 12.971891"
                      onBlur={() => {
                        const val = form.getFieldValue("latitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="longitude"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Longitude
                      </span>
                    }
                    required
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.reject("Longitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid longitude");
                          if (value < -180 || value > 180)
                            return Promise.reject("Longitude must be between -180 and 180");
                          return Promise.resolve();
                        },
                      },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-180}
                      max={180}
                      step={0.000001}
                      placeholder="e.g. 77.594563"
                      onBlur={() => {
                        const val = form.getFieldValue("longitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 4: Facilities */}
            <div style={getStepStyle(4)}>
              <div className="stall-facilities-container">
                {/* Quick Add Facilities Section */}
                <div className="stall-facilities-section">
                  <div className="stall-facilities-section-title">
                    <span className="stall-label-with-badge">
                      <span className="stall-required-star">*</span>
                      Quick Add Facilities
                    </span>
                    <span className="stall-section-hint">Click to add</span>
                  </div>
                  <div className="stall-quick-facilities-grid">
                    {QUICK_FACILITIES.map((facility) => (
                      <Button
                        key={facility}
                        size="small"
                        className={`stall-quick-facility-btn ${facilitiesList.includes(facility) ? 'added' : ''} ${clickedFacility === facility ? 'clicked' : ''}`}
                        onClick={() => addQuickFacility(facility)}
                        icon={facilitiesList.includes(facility) ? <CheckOutlined /> : <PlusOutlined />}
                      >
                        {facility}
                        {facilitiesList.includes(facility) && <span className="facility-check-mark">✓</span>}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Custom Facilities Input */}
                <div className="stall-facilities-section">
                  <div className="stall-facilities-section-title">
                    Custom Facilities
                  </div>
                  <div className="stall-custom-facility-input">
                    <Input
                      placeholder="Enter a custom facility (e.g., 24/7 Security, Elevator Access, AC)"
                      value={customFacility}
                      onChange={(e) => setCustomFacility(e.target.value)}
                      onPressEnter={addCustomFacility}
                      className="stall-custom-input"
                    />
                    <Button
                      type="primary"
                      onClick={addCustomFacility}
                      className="stall-add-custom-btn"
                    >
                      Add Facility
                    </Button>
                  </div>
                </div>

                {/* Current Facilities List */}
                <div className="stall-facilities-section">
                  <div className="stall-facilities-section-title">
                    Current Facilities
                    <span className="stall-facilities-count">{facilitiesList.length}</span>
                  </div>
                  {facilitiesList.length > 0 ? (
                    <div className="stall-current-facilities">
                      {facilitiesList.map((facility, index) => (
                        <Tag
                          key={index}
                          closable
                          onClose={() => removeFacility(facility)}
                          className="stall-facility-tag"
                          icon={<CheckOutlined />}
                        >
                          {facility}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <div className="stall-no-facilities">
                      <p>No facilities added yet</p>
                      <small>Click on quick add buttons or type custom facilities above</small>
                    </div>
                  )}
                </div>

              
              </div>
            </div>

            {/* Step 5: Contact */}
            <div style={getStepStyle(5)}>
              <Form.Item
                name="contactName"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Contact Person Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter contact name" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Rajesh Kumar" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Phone Number
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter a phone number" },
                      { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input maxLength={10} placeholder="e.g. 9876543210" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label={
                      <span className="stall-label-with-badge">
                        <span className="stall-required-star">*</span>
                        Email Address
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. rajesh@company.com" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 6: Images */}
            <div style={getStepStyle(6)}>
              <Form.Item
                required
                name="thumbnail"
                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Thumbnail
                    <span className="stall-badge-primary">Main Image</span>
                  </span>
                }
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList || []}
                rules={[
                  {
                    validator: () => {
                      if (!uploadedThumb) {
                        return Promise.reject("Please upload a thumbnail image");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={thumbnailError ? "error" : ""}
                help={thumbnailError ? "Please upload a thumbnail image" : ""}
              >
                <Upload.Dragger
                  maxCount={1}
                  accept="image/png,image/jpeg"
                  className="stall-upload"
                  beforeUpload={(file) => {

                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    // ✅ MAX 5 CHECK
                    if (imageFileList.length >= 5) {
                      message.error("Maximum 5 images allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadThumbnail(file);

                    return false;
                  }}
                  onChange={() => {
                    setThumbnailError(false);
                  }}
                >
                  <div className="stall-upload-inner">
                    <p className="stall-upload-title">Upload Thumbnail</p>
                    <p className="stall-upload-hint">This is the first image users see · PNG, JPEG</p>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item
                required
                name="images"

                label={
                  <span className="stall-label-with-badge">
                    <span className="stall-required-star">*</span>
                    Stall Images
                    <span className="stall-badge-secondary">Up to 5</span>
                  </span>
                }

                rules={[
                  {
                    validator: () => {
                      if (!uploadedImages || uploadedImages.length === 0) {
                        return Promise.reject("Please upload at least one image");
                      }
                      if (uploadedImages.length > 5) {
                        return Promise.reject("Maximum 5 images allowed");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={imagesError ? "error" : ""}
                help={imagesError ? "Please upload at least one image of the stall" : ""}
              >
                <Upload.Dragger
                  fileList={imageFileList}
                  listType="picture-card"
                  multiple
                  accept="image/png,image/jpeg"
                  className="stall-upload"
                  beforeUpload={(file, fileList) => {
                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadImages([file]);

                    return false;
                  }}
                  onRemove={(file) => {
                    removeImage(file.public_id);
                  }}
                >
                  {imageFileList.length >= 5 ? null : (
                    <div className="stall-upload-inner">
                      <p className="stall-upload-title">
                        Upload Stall Images
                      </p>

                      <p className="stall-upload-hint">
                        Add up to 5 images · PNG, JPEG
                      </p>
                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>

            {/* Navigation Buttons */}
            <div className="stall-btns">
              {current > 0 && (
                <Button
                  onClick={prev}
                  className="stall-btn-prev"
                  disabled={autoDetecting}
                >
                  ← Previous
                </Button>
              )}
              <div className="stall-btns-right">
                {current < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    className="stall-btn-next"
                    disabled={autoDetecting}
                  >
                    Next →
                  </Button>
                )}
                {current === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      if (!uploadedThumb) {
                        setThumbnailError(true);
                        message.error("Please upload a thumbnail image");
                        return;
                      }

                      if (!uploadedImages || uploadedImages.length === 0) {
                        setImagesError(true);
                        message.error("Please upload at least one image");
                        return;
                      }

                      setThumbnailError(false);
                      setImagesError(false);
                      form.submit();
                    }}
                    loading={loading}
                    disabled={loading || uploading}
                    className="stall-btn-submit"
                  >
                    Submit Listing
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default AddStall;